<template>
  <modal @closeModal="closeModal" class="modal-container">
    <div slot="header" class="help-header flex justify-between items-center">
        <span class="help">Article Tracker: A Comprehensive Guide </span>
        <i class="fas fa-times link-pointer" @click="closeModal"></i>
    </div>
    <div slot="body" class="modal-body">

      <div>
        <h4 class="font-bold">Purpose:</h4>
        <span class="block">Article Tracker is a tool designed to help users efficiently manage and track articles.</span>
        <span class="block">It allows users to search for articles by URL, filter results based on specific projects, and identify URLs that may be variants of the original URL. </span>
      </div>

      <div>
        <h4 class="font-bold pt30">Getting Started: </h4>
        <span class="block custom-dot">
          Input the URLs you want to track ensuring each URL starts with its respective protocol (HTTP or HTTPS).
        </span>
        <span class="block custom-dot">
          Duplicated URLs will be detected and show as unique entries when the debug is completed.
        </span>
        <span class="block custom-dot">
          The maximum number of URLs per search is {{ maxTrackerUrls }}.
        </span>
      </div>

      <div>
        <h4 class="font-bold pt30">Filtering Options: </h4>
        <p><span class="underline">Filter by Project:</span> Choose whether to search for URLs across all projects in Bodhi or select a specific project to analyze if the articles are relevant to that project/client. </p>
        <p><span class="underline">Filter by Date:</span> Utilize the date filter to broaden the search for URL variants within the specified time period, if such variants exist.</p>
        <p><span class="underline">URLs to Debug:</span> Enter each URL to debug as separate paragraphs. Do not use spaces or commas. Paste each URL on a separate line by pressing "Enter". </p>
      </div>

      <div>
        <h4 class="font-bold pt30">Understanding URL Variants: </h4>
        <span class="block">URL variants are alternative versions of the original URL:</span>
        <span class="block">https://thisisatest.com/carmacrawler/103334/</span>
        <span class="block">Which may contain additional parameters or tracking information:</span>
        <p >https://thisisatest.com/carmacrawler/103334/<span class="text-italic">source=newsletter</span></p>
        <p class="m4"><span class="underline">Article Tracker</span> can identify and display these URL variants within the specified timeframes selected on the date picker. </p>
      </div>

      <div>
        <h4 class="font-bold pt30">Troubleshooting: </h4>
        <p>For every result retrieved (assuming all are found), user will be provided with three distinct IDs: <span class="text-italic">Bodhi ID, Insight ID, and Crawler ID.</span></p>
        <p>Each ID is linked and accessible to users.</p>
        <p>- The <span class="underline">Bodhi ID</span> IDs listed can be opened and missed projects added by users manually.</p>
        <p>- The <span class="underline">Insight ID</span> lists the projects the article is delivered and can be visited by users.</p> 
        <p>- The <span class="underline">Crawler ID</span> is exclusively for internal use by the Content Ingestion team.</p>
        <p>- The <span class="underline">Provider</span> column lists the source where that article URL has come from: if by the crawler it will say <span class="text-italic">online</span> or  <span class="text-italic">online paywall</span>; if manually added it will say <span class="text-italic">Manual Upload</span> or <span class="text-italic">lexis Nexis Manual</span>; if <span class="text-italic">eclips</span> it means it has been included by the eclips (NLA) feed;</p>
        <p>- The <span class="underline">Created at & Published</span> at columns will show the date and time in UTC in which the articles were <span class="text-italic">Created</span> on our system. And <span class="text-italic">Published</span> refers to the date and time the article was published by the source; </p>
      </div>
        
      <div>
        <h4 class="font-bold pt30">Filter by:</h4>
        <span class="custom-dot block font-bold">All</span>
        <p>Will show all results of articles <span class="underline">found</span> and <span class="underline">not found</span> with <span class="underline">all the variant URLs</span></p>

        <span class="custom-dot block font-bold">Found</span>
        <p>Lists only the Bodhi ID and Insight IDs where the article was found and/or has been a project match for. This could involve one or multiple projects that will be listed under Insight ID.</p>
        <p><span class="underline">Note:</span> If the user is testing across a particular project and the result is listed as <span>project_name NOT A MATCH</span> – the user can open article on Bodhi and seamlessly add it manually to their client.</p>
        <p>The user, in this case, should contact Project Operation team to investigate why their project/client was missed by submitting a Jira support ticket– there might be a need to check the text extraction was the correct one at the time articles was picked by the crawler, the date or matching keywords are correct or other possible issue.</p>
        <span class="custom-dot block font-bold">Not Found</span>
        <p>This indicates that the crawler has not picked up this article yet nor any manual upload of that article has occurred. The Content Support team should be informed via Jira to check the source and add/ update the source on the crawler.</p>

        <span class="custom-dot block font-bold">URL Exact Match</span>
        <p>This filter will show you ALL results but with no variant URLs, just the exact URL match as the user inserted on the Search box.</p>
      </div>

      <div>
        <h4 class="font-bold pt30">Additional Tips:</h4>
        <span class="block">Utilize the filtering options to narrow down search results and focus on relevant articles.</span>
        <span class="block">Contact POT or Content Support teams for assistance with troubleshooting or specific queries on <a class="link-support" href="https://carma-it.atlassian.net/servicedesk/customer/portals" target="_blank">CARMA Support Centre</a>.</span> 
      </div>
    </div>
  </modal>
</template>
  
<script>
  import modal from '../modal.vue';
  import { MAX_TRACKER_URLS } from '../../config/const';

  export default {
    name: 'articles-tracker-info-modal',

    data() {
      return {
        maxTrackerUrls: MAX_TRACKER_URLS
      };
    },

    methods: {
      closeModal(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        this.$emit('closeModal');
      }
    },

    components: {
      modal
    }
  }
</script>

<style lang="scss" scoped>
:deep(.modal-container) {
  min-width: 700px !important;
  min-height: 400px;
}

ul {
  li {
    font-weight: 300;
  }
}

.help-header {
  i {
    color: $gray-400;
    font-weight: 900;
  }

  color: $orange;
}

.modal-container {
  .modal-body {
    height: 500px;
    overflow: auto;

    .underline {
      text-decoration: underline;
    }

    ol {
      list-style: decimal;
      padding-left: 15px;

      li ol {
        list-style: upper-roman;
      }
    }
  }
}

.link-support{
  color: $carma-teal;
}
</style>